import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react'
import { useCustomerAuth } from '../customer-auth'
import { withLocalePrefix, type ShopLocale } from './shopLocale'
import { useShopFormatters } from './provider/useShopFormatters'
import { useShopLocaleState } from './provider/useShopLocaleState'
import { createTranslator } from './provider/translate'
import type { ShopI18nContextValue } from './provider/types'

const ShopI18nContext = createContext<ShopI18nContextValue | null>(null)

type ShopI18nProviderProps = {
  children: ReactNode
  initialLocale?: ShopLocale
  availableLocales?: ShopLocale[]
}

export const ShopI18nProvider = ({ children, initialLocale, availableLocales }: ShopI18nProviderProps) => {
  const { profile } = useCustomerAuth()
  const { locale, language, availableLocales: resolvedAvailableLocales, setLocale, setLanguage } = useShopLocaleState({
    initialLocale,
    profileLocale: profile?.language ?? null,
    availableLocales,
  })

  const t = useMemo(() => createTranslator(locale), [locale])
  const { formatCurrency, formatDateTime } = useShopFormatters({ locale, t })
  const toLocalizedPath = useCallback(
    (path: string) => withLocalePrefix(path, locale, { availableLocales: resolvedAvailableLocales }),
    [locale, resolvedAvailableLocales],
  )

  const value = useMemo<ShopI18nContextValue>(
    () => ({
      locale,
      language,
      availableLocales: resolvedAvailableLocales,
      t,
      formatCurrency,
      formatDateTime,
      toLocalizedPath,
      setLocale,
      setLanguage,
    }),
    [formatCurrency, formatDateTime, language, locale, resolvedAvailableLocales, setLanguage, setLocale, t, toLocalizedPath],
  )

  return <ShopI18nContext.Provider value={value}>{children}</ShopI18nContext.Provider>
}

export const useShopI18n = () => {
  const context = useContext(ShopI18nContext)
  if (!context) {
    throw new Error('useShopI18n deve essere usato dentro ShopI18nProvider')
  }
  return context
}
